/**
 * Internationalization (i18n) Configuration
 * Centralized text strings for the application
 * 
 * Usage:
 * import { i18n } from '@/config/i18n';
 * <Button>{i18n.common.buttons.save}</Button>
 */

export const i18n = {
  // Common strings used across the application
  common: {
    appName: 'ProbX',
    buttons: {
      save: 'Save',
      cancel: 'Cancel',
      delete: 'Delete',
      edit: 'Edit',
      create: 'Create',
      submit: 'Submit',
      close: 'Close',
      confirm: 'Confirm',
      back: 'Back',
      next: 'Next',
      retry: 'Retry',
      refresh: 'Refresh',
      search: 'Search',
      filter: 'Filter',
      export: 'Export',
      import: 'Import',
      download: 'Download',
      upload: 'Upload',
      add: 'Add',
      remove: 'Remove',
      clear: 'Clear',
      apply: 'Apply',
      reset: 'Reset',
      viewDetails: 'View Details',
      saveConfiguration: 'Save Configuration',
      addConfiguration: 'Add Configuration',
      logOut: 'Logout',
    },
    labels: {
      username: 'Username',
      password: 'Password',
      email: 'Email',
      name: 'Name',
      description: 'Description',
      status: 'Status',
      type: 'Type',
      date: 'Date',
      time: 'Time',
      dateRange: 'Date Range',
      actions: 'Actions',
      search: 'Search',
      filter: 'Filter',
      sort: 'Sort',
      loading: 'Loading',
      noData: 'No Data',
      error: 'Error',
      success: 'Success',
      warning: 'Warning',
      info: 'Info',
      rows: 'Rows',
    },
    placeholders: {
      enterUsername: 'Please enter username',
      enterPassword: 'Please enter password',
      enterEmail: 'Enter email address',
      enterName: 'Enter name',
      enterDescription: 'Enter description',
      search: 'Search...',
      selectOption: 'Select an option',
      enterUrl: 'https://www.example.com/',
      enterUrlExample: 'E.g. https://example.com or 80.75.11.12 or example.com',
      enterClientName: 'Enter client name',
    },
    messages: {
      loading: 'Loading...',
      saving: 'Saving...',
      deleting: 'Deleting...',
      processing: 'Processing...',
      noResults: 'No results found',
      noData: 'No data available',
      error: 'An error occurred',
      success: 'Operation completed successfully',
      confirmDelete: 'Are you sure you want to delete this item?',
      unsavedChanges: 'You have unsaved changes. Are you sure you want to leave?',
      underConstruction: 'Under Construction',
    },
    validation: {
      required: 'This field is required',
      invalidEmail: 'Please enter a valid email address',
      invalidUrl: 'Please enter a valid URL',
      invalidHttpsUrl: 'URL must use HTTPS protocol (https://)',
      minLength: 'Minimum length is {min} characters',
      maxLength: 'Maximum length is {max} characters',
      passwordMismatch: 'Passwords do not match',
    },
    badges: {
      suggested: 'Suggested',
      suggestedTooltip: 'Suggested based on selected timezone',
    },
  },

  // Login page
  login: {
    title: 'Welcome to {appName}',
    subtitle: 'Enter your email and password to continue',
    needHelp: 'Need Help?',
    forgotPasswordLink: 'Forgot Password?',
    loginButton: 'Request Passkey',
    sendingOtp: 'Sending Passkey...',
    description: 'A Crucial Practice for Incident Response teams to detect and respond to Site Reliability and Security Incidents in Real-Time',
    aria: {
      showPassword: 'Show password',
      hidePassword: 'Hide password',
    },
    errors: {
      emailRequired: 'Email is required',
      passwordRequired: 'Password is required',
      otpSendFailed: 'Failed to send Passkey. Please try again.',
      invalidOtp: 'Invalid Passkey. Please try again.',
    },
    success: {
      otpSent: 'Passkey sent to your registered email',
      welcomeBack: 'Welcome back {name}!',
    },
    otp: {
      title: 'Enter Verification Code',
      description: "We've sent a 6-digit code to your registered email for ",
      placeholder: 'Enter 6-digit Passkey',
      verifying: 'Verifying...',
      verifyButton: 'Verify Passkey',
      codeExpires: 'Code expires in {time}',
      expired: 'Passkey has expired. Please try logging in again.',
    },
    passkey: {
      title: 'Welcome to {appName}',
      subtitle: 'Please check your email for your verification code.',
      label: 'Passkey',
      resendPrefix: 'Resend Passkey in',
      resend: 'Resend Passkey',
      loginButton: 'Login',
      backButton: 'Back',
      errors: {
        invalidLength: 'Please enter a 6-character passkey',
      },
    },
    forgotPassword: {
      title: 'Forgot Password?',
      subtitle: "No worries! Enter your email address below and we'll send you instructions to reset your password",
      emailLabel: 'Email',
      emailPlaceholder: 'Enter your email address',
      sendButton: 'Send Reset Link',
      backButton: 'Back',
      success: 'Reset link sent to your email',
      errors: {
        emailRequired: 'Email is required',
        invalidEmail: 'Please enter a valid email address',
        sendFailed: 'Failed to send reset link. Please try again.',
      },
    },
    resetPassword: {
      title: 'Reset Password',
      subtitle: 'Please enter your new password below. Make sure it meets the security requirements.',
      newPasswordLabel: 'New Password',
      confirmPasswordLabel: 'Confirm Password',
      resetButton: 'Reset Password',
      backButton: 'Back',
      requirements: {
        title: 'Password Requirements:',
        minLength: 'At least 8 characters',
        uppercase: 'One uppercase letter',
        lowercase: 'One lowercase letter',
        digit: 'One digit',
      },
      errors: {
        passwordRequired: 'Password is required',
        confirmPasswordRequired: 'Please confirm your password',
        passwordMismatch: 'Passwords do not match',
        passwordTooShort: 'Password must be at least 8 characters',
        passwordNoUppercase: 'Password must contain at least one uppercase letter',
        passwordNoLowercase: 'Password must contain at least one lowercase letter',
        passwordNoDigit: 'Password must contain at least one digit',
        passwordNoSpecialChar: 'Password must contain at least one special character',
        resetFailed: 'Failed to reset password. Please try again.',
      },
      success: 'Password reset successfully',
    },
  },

  // Navbar
  navbar: {
    companyName: 'SharkNinja',
    defaultUserName: 'Guest',
  },

  // Notifications
  notifications: {
    title: 'NOTIFICATIONS',
    markAllAsRead: 'Mark all as read',
    noNotifications: 'No notifications',
    emptyState: 'You have no notifications at this time',
    loading: 'Loading notifications...',
    error: 'Failed to load notifications',
    deleteSuccess: 'Notification deleted successfully',
    markReadSuccess: 'Notification marked as read',
    markUnreadSuccess: 'Notification marked as unread',
    markAllReadSuccess: 'All notifications marked as read',
    deleteAllSuccess: 'All notifications deleted successfully',
    types: {
      alert: 'Alert',
      incident: 'Incident',
      threshold: 'Threshold',
    },
    actions: {
      markAsRead: 'Mark as read',
      markAsUnread: 'Mark as unread',
      delete: 'Delete',
    },
  },

  // Monitors page
  monitors: {
    title: 'Monitors',
    subtitle: 'Monitor your websites and APIs',
    dashboard: {
      title: 'Monitors',
      description: 'Track uptime, response time, and health status for your monitored sites',
      metrics: {
        sitesMonitored: 'Sites Monitored',
        sitesScraped: 'Sites Scraped',
        logsAnalysed: 'Logs Analysed',
        openIncidents: 'Open Incidents',
        averageResponseTime: 'Average Response Time',
      },
      tabs: {
        httpWebsites: 'Uptime',
        scraper: 'Page Insight',
        syntheticTest: 'Synthetic Test',
        logAnalyser: 'Log Analyser',
      },
    },
    syntheticTest: {
      title: 'Synthetic Test',
      searchPlaceholder: 'Search synthetic tests',
      addButton: 'Add',
      sortLabel: 'Sort',
      sortOptions: {
        aToZ: 'A-Z',
        zToA: 'Z-A',
        status: 'Status',
        lastRun: 'Last Run',
      },
    },
    defaultTitle: 'Monitor',
    createTitle: 'Create Monitor',
    createSubtitle: 'Add a single monitor to track your website or service uptime.',
    buttons: {
      addMonitor: 'Add Monitor',
      editMonitor: 'Edit Monitor',
      deleteMonitor: 'Delete Monitor',
      viewDetails: 'View Details',
      createMonitor: 'Create monitor',
    },
    labels: {
      url: 'URL',
      friendlyName: 'Friendly Name',
      monitorName: 'Monitor name (optional)',
      monitorType: 'Monitor type',
      urlToMonitor: 'URL to monitor',
      keywordToLookFor: 'Keyword to look for',
      rule: 'Rule',
      ipOrHost: 'IP or host to monitor',
      tcpPort: 'TCP port',
      status: 'Status',
      interval: 'Interval',
      timeout: 'Timeout',
      regions: 'Regions',
      tags: 'Tags',
      groups: 'Groups',
      responseTime: 'Response Time',
      lastChecked: 'Last Checked',
      timezone: 'Timezone',
      monitoringLocation: 'Monitoring Location',
    },
    filters: {
      title: 'FILTERS',
      scraper: {
        priority: 'Priority',
        pageType: 'Page Type',
        status: 'Status',
        datePicker: 'Date Picker',
        lastScrapedFrom: 'Last Scraped From',
        lastScrapedTo: 'Last Scraped To',
        lastScrapedPreset: 'Last Scraped Preset',
        resetFilters: 'Reset Filters',
        applyFilters: 'Apply Filters',
      },
      syntheticTest: {
        status: 'Status',
        type: 'Type',
        placeholders: {
          status: 'Choose an option',
          type: 'Choose an option',
        },
      },
      placeholders: {
        priority: 'Choose an option',
        pageType: 'Choose an option',
        status: 'Choose an option',
        dateRange: 'Start Date - End Date',
        lastScrapedPreset: 'Select preset',
      },
      options: {
        all: 'All',
        allPriorities: 'All Priorities',
        allPageTypes: 'All Page Types',
        allStatuses: 'All Statuses',
        high: 'High',
        medium: 'Medium',
        low: 'Low',
        active: 'Active',
        inactive: 'Inactive',
        running: 'Running',
        homepage: 'Homepage',
        home: 'Home',
        plp: 'Category/Listing Page (PLP)',
        pdp: 'E-commerce Product Page (PDP)',
        product: 'Product',
        category: 'Category Landing Page (CLP)',
        search: 'Search Results',
        checkout: 'Checkout Flow',
        unknown: 'Unknown',
        account: 'Account',
        today: 'Today',
        thisWeek: 'This Week',
        thisMonth: 'This Month',
        last7Days: 'Last 7 Days',
        last30Days: 'Last 30 Days',
      },
    },
    stats: {
      overview: 'Overview',
      last24Hours: 'Last 24 hours',
      monitoredSites: 'Monitored sites',
      openIncidents: 'Open incidents',
      successRate: 'Success rate',
      totalChecks: 'Total checks',
      successful: 'Successful',
      failed: 'Failed',
      avgResponseTime: 'Avg response time',
    },
    statusLabels: {
      up: 'Up',
      down: 'Down',
      paused: 'Paused',
    },
    sortOptions: {
      upFirst: 'Up first',
      pausedFirst: 'Paused first',
      aToZ: 'A → Z',
      newestFirst: 'Newest first',
    },
    filterOptions: {
      status: 'Status',
    },
    bulkActions: {
      title: 'Bulk Actions',
      startAll: 'Start All',
      pauseAll: 'Pause All',
      deleteAll: 'Delete All',
      confirmTitle: 'Confirm Bulk Action',
      confirmMessage: 'Are you sure you want to {action} {count} monitor{plural}?',
      confirmStart: 'Start',
      confirmPause: 'Pause',
      confirmDelete: 'Delete',
    },
    monitorTypes: {
      http: 'HTTP / website monitoring',
      keyword: 'Keyword monitoring',
      ping: 'Ping monitoring',
      port: 'Port monitoring',
    },
    keywordRules: {
      exists: 'Start incident when keyword exists',
      missing: 'Start incident when keyword missing',
    },
    sslDomain: {
      title: 'SSL certificate and Domain checks',
      checkSslErrors: 'Check SSL errors',
      sslExpiryReminders: 'SSL expiry reminders',
      domainExpiryReminders: 'Domain expiry reminders',
    },
    values: {
      never: 'Never',
    },
    placeholders: {
      search: 'Search',
      enterUrl: 'https://www.example.com/',
      enterFriendlyName: 'Enter friendly name',
      sortBy: 'Sort by',
      selectMonitorType: 'Select monitor type',
      selectRule: 'Select rule',
      marketingSite: 'e.g. Marketing site',
      httpsPlaceholder: 'https://',
      keywordPlaceholder: 'e.g. availability',
      ipHostPlaceholder: 'E.g. 80.75.11.12 or example.com',
      urlIpHostPlaceholder: 'E.g. https://example.com or 80.75.11.12 or example.com',
      tcpPortPlaceholder: 'E.g. 22',
      selectTimezone: 'Select timezone',
      selectRegion: 'Select region',
    },
    help: {
      keywordHelp: 'The keyword must be present in the response HTML/JSON.',
    },
    checkboxes: {
      caseSensitive: 'Case-sensitive check',
    },
    emailActions: {
      title: 'Notification Types',
      alert: 'Alert',
      incident: 'Incident',
      threshold: 'Threshold',
    },
    modals: {
      deleteTitle: 'Delete Monitor',
      deleteMessage: 'Are you sure you want to delete this monitor? All associated alerts and incidents will be archived.',
      deleteWarning: 'This action cannot be undone.',
      filterTitle: 'Filter',
      confirmDelete: 'Confirm Delete',
      reset: 'Reset',
      apply: 'Apply',
    },
    messages: {
      noMonitors: 'No monitors found',
      noSyntheticTests: 'No synthetic tests found',
      syntheticTestEmptyHint: 'Synthetic tests will appear here when configured',
      tryAdjusting: 'Try adjusting your search or filter criteria',
      loadingMonitors: 'Loading monitors...',
      failedToFetch: 'Failed to fetch monitors',
      monitorCreated: 'Monitor created successfully!',
      monitorUpdated: 'Monitor updated successfully',
      monitorDeleted: 'Monitor deleted successfully',
      monitorPaused: 'Monitor paused successfully',
      monitorStarted: 'Monitor started successfully',
      failedToPause: 'Failed to pause monitor',
      failedToStart: 'Failed to start monitor',
      failedToDelete: 'Failed to delete monitor',
      failedToCreate: 'Failed to create monitor. Please try again.',
      bulkActionSuccess: '{successful} of {total} monitors {action} successfully',
      bulkActionFailed: 'Failed to {action} monitors',
      loadingDetails: 'Loading monitor details...',
      failedToFetchDetails: 'Failed to load monitor details',
      monitorNotFound: 'Monitor not found',
      switchedToLocalTime: 'Switched to Local Time',
      switchedToSiteTime: 'Switched to Site Time ({timezone})',
      failedToUpdate: 'Failed to update timezone preference',
    },
    validation: {
      urlRequired: 'URL is required',
      invalidHttpsUrl: 'URL must use HTTPS protocol (https://)',
    },
    tooltips: {
      monitoringInterval: 'How often the system checks website availability and performance',
      requestTimeout: 'Maximum time to wait for a website response before alerting',
      responseTimeThreshold: 'Response time limit that triggers a performance alert when exceeded',
      statusCodes: 'Select status codes that should trigger an alert',
      consecutiveBreaches: 'Number of consecutive threshold breaches required to trigger an alert',
      monitoringLocation: 'Geographic region where monitoring checks will be performed',
    },
    detail: {
      title: 'Monitor Details',
      backToMonitors: 'Back to Monitors',
      sections: {
        configuration: 'Configuration',
        uptimeStats: 'Uptime Statistics',
        responseTime: 'Response Time',
        activityLog: 'Activity Log',
        patternAnalysis: 'Pattern Analysis',
      },
      configLabels: {
        url: 'URL',
        name: 'Name',
        status: 'Status',
        enabled: 'Enabled',
        frequency: 'Check Frequency',
        timeout: 'Timeout',
        createdAt: 'Created At',
        updatedAt: 'Last Updated',
        everyMinutes: 'Every {minutes} minutes',
        seconds: '{seconds} seconds',
      },
      uptimeLabels: {
        period: 'Period',
        uptime: 'Uptime',
        totalIncidents: 'Total Incidents',
        totalDowntime: 'Total Downtime',
        currentStatus: 'Current Status',
        statusDuration: 'Status Duration',
        last24Hours: 'Last 24 Hours',
        last7Days: 'Last 7 Days',
        last30Days: 'Last 30 Days',
        last365Days: 'Last 365 Days',
      },
      activityLabels: {
        time: 'Time',
        status: 'Status',
        responseTime: 'Response Time',
        statusCode: 'Status Code',
        message: 'Message',
        noActivity: 'No activity logs available',
        loadingActivity: 'Loading activity logs...',
      },
      chartLabels: {
        dayQuarter: 'Day Quarter',
        count: 'Count',
      },
      activityLog: {
        title: 'Activity Logs',
        filters: {
          status: 'Status',
          statusCode: 'Status Code',
          origin: 'Origin',
          responseTimeRange: 'Response Time Range',
          minRange: 'Min Range',
          maxRange: 'Max Range',
          limit: 'Limit',
          placeholders: {
            status: 'Choose an option{Success/Failed}',
            statusCode: 'Choose a code',
            origin: 'Choose an option{Canada/United States}',
            minRange: 'Min Range',
            maxRange: 'Max Range',
            limit: 'Enter limit',
          },
          options: {
            all: 'All',
            success: 'Success',
            failed: 'Failed',
            canada: 'Canada',
            unitedStates: 'United States',
          },
        },
        export: 'Export',
        exportToCsv: 'Export to CSV',
      },
      actions: {
        edit: 'Edit Monitor',
        delete: 'Delete Monitor',
        pause: 'Pause',
        start: 'Start Monitor',
        notify: 'Notify',
        testNotification: 'Test Notification',
      },
      labels: {
        httpMonitorFor: 'HTTP/S monitor for',
        lastCheck: 'Last Check',
        checkedEvery: 'Checked every ',
        incidentsDown: '{incidents} incidents, {downtime} down',
        currentlyFor: 'Currently {status} for {duration}',
        checksTimes: 'Checked {count} times today',
        domainValidUntil: 'Domain Valid Until',
        domainStatus: 'Domain Status',
        sslCertificateValidUntil: 'SSL Certificate Valid Until',
        sslCertificateStatus: 'SSL Certificate Status',
        toBeNotified: 'To Be Notified',
        valid: 'Valid',
        expired: 'Expired',
        noChecksYet: 'No checks yet',
        createdBy: 'Created by {user} on {date}',
        maintenanceWindow: 'Maintenance Window',
        clickToConfigure: 'Click to configure',
        noMaintenance: 'No maintenance scheduled',
        upcoming: 'Upcoming',
        inProgress: 'In Progress',
      },
      maintenanceWindow: {
        title: 'Maintenance Window',
        description: 'Configure the website maintenance window and see the history',
        labels: {
          websiteUrl: 'Website URL',
          emailRecipient: 'Email Recipient',
          startDate: 'Start date',
          startTime: 'Start time',
          endDate: 'End date',
          endTime: 'End time',
          comments: 'Comments',
          maintenanceHistory: 'MAINTENANCE HISTORY',
        },
        placeholders: {
          startDate: 'Time Picker',
          startTime: 'Time Picker',
          endDate: 'Time Picker',
          endTime: 'Time Picker',
          comments: 'Comments',
          editingDisabled: 'Editing Disabled',
        },
        buttons: {
          schedule: 'Schedule Maintenance',
          cancel: 'Cancel',
          edit: 'Edit Maintenance Window',
          update: 'Update',
          delete: 'Delete',
        },
        messages: {
          scheduled: 'Maintenance window scheduled successfully',
          failedToSchedule: 'Failed to schedule maintenance window',
          updated: 'Maintenance window updated successfully',
          failedToUpdate: 'Failed to update maintenance window',
          deleted: 'Maintenance window deleted successfully',
          failedToDelete: 'Failed to delete maintenance window',
          noHistory: 'No maintenance history available',
          confirmDelete: 'Are you sure you want to delete this maintenance window? This action cannot be undone.',
          systemUnderMaintenance: 'System is currently under scheduled maintenance',
          maintenanceEndsAt: 'Maintenance ends at',
        },
        validation: {
          startDateRequired: 'Start date is required',
          startTimeRequired: 'Start time is required',
          endDateRequired: 'End date is required',
          endTimeRequired: 'End time is required',
          endDateAfterStart: 'End date must be after start date',
          endTimeAfterStart: 'End time must be after start time when dates are the same',
          startTimeNotPast: 'Start time cannot be in the past',
          endTimeNotPast: 'End time cannot be in the past',
        },
      },
    },
  },

  // Settings
  settings: {
    title: 'Settings',
    subtitle: 'Manage your account settings to control access.',
    tabs: {
      password: 'Password',
      userManagement: 'User Management',
      organizationSettings: 'Organization settings',
    },
    buttons: {
      settings: 'Settings',
      updatePassword: 'Update Password',      addUser: 'Add',
      editUser: 'Edit User',
    },
    password: {
      title: 'Manage Password',
      currentPassword: 'Current Password',
      newPassword: 'New Password',
      confirmPassword: 'Confirm Password',
      placeholders: {
        currentPassword: 'Enter current password',
        newPassword: 'Enter new password',
        confirmPassword: 'Confirm new password',
      },
      messages: {
        passwordUpdated: 'Password updated successfully',
        passwordUpdateFailed: 'Failed to update password. Please try again.',
        passwordMismatch: 'Passwords do not match',
        currentPasswordRequired: 'Current password is required',
        newPasswordRequired: 'New password is required',
        confirmPasswordRequired: 'Please confirm your password',
      },
    },
    userManagement: {
      title: 'Manage users',
      searchPlaceholder: 'Search',
      filterLabel: 'Filter',
      filterLabels: {
        status: 'Status',
      },
      filterPlaceholders: {
        status: 'Select status',
      },
      columns: {
        id: 'ID',
        email: 'Email',
        name: 'Name',
        role: 'Role',
        dateAdded: 'Date Added',
        status: 'Status',
        action: 'Action',
      },
      status: {
        accepted: 'Accepted',
        pending: 'Pending',
        inactive: 'Inactive',
        active: 'Active',
      },
      timeWindowButtons: {
        accepted: 'Accepted',
        pending: 'Pending',
      },
      messages: {
        userAdded: 'User added successfully',
        userUpdated: 'User updated successfully',
        userAddFailed: 'Failed to add user. Please try again.',
        userUpdateFailed: 'Failed to update user. Please try again.',
        userDeleted: 'User deleted successfully',
        userActivated: 'User activated successfully',
        userDeactivated: 'User deactivated successfully',
        userToggleStatusFailed: 'Failed to update user status. Please try again.',
        roleDowngradeNotAllowed: 'Cannot downgrade role for super admins or yourself.',
        noUsers: 'No users found',
        confirmDeleteTitle: 'Delete User',
        confirmDeleteMessage: 'Are you sure you want to delete user "{email}"? This is a permanent action and cannot be undone.',
        confirmDeleteButton: 'Delete',
      },
      actions: {
        activate: 'Activate',
        deactivate: 'Deactivate',
      },
    },
    addEditUser: {
      title: {
        add: 'Add User',
        edit: 'Edit User',
      },
      labels: {
        email: 'Email',
        firstName: 'First Name',
        lastName: 'Last Name',
        role: 'Role',
      },
      placeholders: {
        email: 'Enter email',
        firstName: 'Enter first name',
        lastName: 'Enter last name',
        role: 'Select role',
      },
      roles: {
        superAdmin: 'Super Admin',
        admin: 'Admin',
        user: 'User',
      },
      validation: {
        emailRequired: 'Email is required',
        invalidEmail: 'Please enter a valid email address',
        firstNameRequired: 'First name is required',
        lastNameRequired: 'Last name is required',
        roleRequired: 'Role is required',
      },
    },
    organizationSettings: {
      title: 'Manage Organization',
      nonSuperAdminTitle: 'Organization Details',
      organizationName: 'Organization Name',
      placeholders: {
        organizationName: 'Enter organization name',
      },
      buttons: {
        uploadImage: 'Upload Image',
        deleteLogo: 'Delete Logo',
        update: 'Update',
      },
      messages: {
        organizationNameRequired: 'Organization name is required',
        organizationUpdated: 'Organization settings updated successfully',
        organizationUpdateFailed: 'Failed to update organization settings. Please try again.',
        imageUploadFailed: 'Failed to upload image. Please try again.',
        imageUploadHelperText: 'Upload a transparent logo in PNG or SVG format for best results.',
        imageUploadSupportedFormatsText: 'Supports: .png, .svg',
        imageUploadMaximumSizeText: 'Maximum size: 5MB',
        imageTooLarge: 'Image file size must be less than 5MB',
        invalidImageFormat: 'Invalid image format. Please use PNG or SVG',
        logoDeleted: 'Logo deleted successfully',
        logoDeleteFailed: 'Failed to delete logo. Please try again.',
        confirmDeleteLogo: 'Are you sure you want to delete the organization logo?',
        logoSelected: 'Logo selected. Click Update to save changes.',
        updateSuccess: 'Organization updated successfully',
        updateError: 'Failed to update organization. Please try again.',
        logoUploadError: 'Failed to upload logo. Please try again.',
      },
    },
  },

  // Configure
  configure: {
    title: 'Website Details',
    subtitle: 'Enter the website URL and basic notification preferences.',
    steps: {
      siteDetails: 'Site Details',
      executionSettings: 'Execution Settings',
      websiteDetails: 'Website Details',
      notificationSettings: 'Notification Settings',
      scenarios: 'Scenarios',
      scheduler: 'Scheduler',
    },
    syntheticTest: {
      title: 'Create Scenarios',
      subtitle: 'Add Scenarios.',
      scenarioNames: {
        login: 'Login',
        plp: 'PLP',
        pdp: 'PDP',
        addToCart: 'Add to Cart',
        checkout: 'Checkout',
      },
      scenarioSteps: 'Scenario Steps',
      addStep: 'Add Step',
      schedulerStepDescription: 'Configure when and how often the synthetic test runs.',
      faqLabel: 'workflow',
      scenarioIconTooltip: 'Workflow',
      dummyStepLabel: 'Step 1',
      chooseWorkflowToExecute: 'Choose workflow to be executed',
      chooseScenariosToExecute: 'Choose scenarios to be executed',
      pageUrl: 'Page URL',
      pageUrlPlaceholder: 'https://',
      shippingAddress: 'Shipping address',
      shippingAddressPlaceholder: 'Enter shipping address',
      addUrl: 'Add URL',
      scenarioOptions: {
        login: {
          successfulLogin: 'Successful Login',
          loginInvalidPassword: 'Login with invalid password or mail',
          loginNonExistentEmail: 'Login with non-existent email',
          loginEmptyEmailPassword: 'Login with empty email and password',
        },
        plp: {
          pageLoadsSuccessfully: 'Page Loads Successfully',
          validatePlpGrid: 'Validate PLP page grid',
          validateProductAvailability: 'Validate product availability on PLP',
          addToCart: 'Add to Cart',
        },
        pdp: {
          validatePdpBasicContent: 'Validate PDP basic content',
          validateProductDetailsOnPdp: 'Validate product details on PDP',
          validateProductImageGalleryOnPdp: 'Validate product image gallery on PDP',
          addToCartForPdp: 'Add to Cart',
        },
        addToCart: {
          viewCartWithAddedProducts: 'View cart with added products',
          updateProductQuantityInCart: 'Update product quantity in cart',
          removeProductFromCart: 'Remove product from cart',
          proceedToCheckoutFromCart: 'Proceed to checkout from cart',
        },
        checkout: {
          accessCheckoutPageSuccessfully: 'Access checkout page successfully',
          enterShippingAddressSuccessfully: 'Enter shipping address successfully',
          viewOrderSummaryDuringCheckout: 'View order summary during checkout',
          selectPaymentMethodSuccessfully: 'Select payment method successfully',
          placeOrder: 'Place Order',
          orderConfirmationPage: 'Order Confirmation Page for checkout',
        },
      },

      formLabels: {
        monitorName: 'Monitor name',
        websiteUrl: 'Website URL',
        login: 'Username',
        password: 'Password',
        scheduleFrequency: 'Schedule Frequency',
        schedulingMode: 'Scheduling mode',
        scheduleTime: 'Schedule Time',
      },
      formPlaceholders: {
        monitorName: 'Text Input',
        login: 'Enter username',
        password: 'Enter password',
        scheduleFrequency: 'Select frequency',
      },
      schedulingModeOptions: {
        default: 'Default',
        custom: 'Custom',
      },
      scheduleFrequencyOptions: {
        onceADay: 'Once a day',
        twiceADay: 'Twice a day',
        fourTimesADay: 'Four times a day',
      },
      testScenarios: {
        title: 'Test scenarios',
        description: 'Run a quick test against the API with your current website and scenario URLs.',
        testLogin: 'Test Login',
        testPlp: 'Test PLP',
        testPdp: 'Test PDP',
        testCart: 'Test Cart',
        testCheckout: 'Test Checkout',
        checkHealth: 'Check service health',
        testRunning: 'Test running…',
        testSuccess: 'Test completed successfully.',
        testFailed: 'Test failed.',
        loginTestSummaryPassed: '{count} login scenario(s) passed.',
        loginTestSummaryMixed: '{passed} passed, {failed} failed.',
        plpTestSummaryPassed: '{count} PLP scenario(s) passed.',
        plpTestSummaryMixed: '{passed} passed, {failed} failed.',
        pdpTestSummaryPassed: '{count} PDP scenario(s) passed.',
        pdpTestSummaryMixed: '{passed} passed, {failed} failed.',
        selectScenarioFirst: 'Please select a scenario above to run a test.',
        enterWebsiteDetailsFirst: 'Complete Website Details (URL, username, password) first.',
        enterWebsiteUrlFirst: 'Please provide a valid Website URL (HTTPS) to run the test.',
        enterPageUrlForPlp: 'Enter Page URL for PLP scenario to run test.',
        enterPageUrlForPdp: 'Enter Page URL for PDP scenario to run test.',
        enterPageUrlForCart: 'Enter Page URL for Cart scenario to run test.',
        enterPageUrlForCheckout: 'Enter Page URL for Checkout scenario to run test.',
        cartTestSummaryPassed: '{count} cart scenario(s) passed.',
        cartTestSummaryMixed: '{passed} passed, {failed} failed.',
        checkoutTestSummaryPassed: '{count} checkout scenario(s) passed.',
        checkoutTestSummaryMixed: '{passed} passed, {failed} failed.',
        serviceHealthy: 'Synthetic monitor service is healthy.',
        serviceUnavailable: 'Synthetic monitor service is unavailable.',
        lastResultTitle: 'Last test result',
        resultScenarioLogin: 'Login',
        resultScenarioPlp: 'PLP',
        resultScenarioPdp: 'PDP',
        resultScenarioCart: 'Cart',
        resultScenarioCheckout: 'Checkout',
        resultPassed: 'Passed',
        resultFailed: 'Failed',
        resultLoggedIn: 'Logged in',
        resultStepsCount: 'Steps',
        resultExecutionTime: 'Execution time',
        resultExecutionTimeMs: '{ms} ms',
        resultRunTestToSee: 'Run a test above .',
      },
    },
    notificationSettings: {
      title: 'Notification Settings',
      subtitle: 'Configure custom notification settings.',
      defaultRecipient: 'Default Notification Recipient',
      customRecipientTitle: 'Custom Recipient',
      alertConfigurations: {
        title: 'Alert Configurations',
        notificationTypes: 'Notification Types',
        httpStatusCodes: 'HTTP Status Codes',
      httpStatusCodesPlaceholder: 'Select HTTP status codes',
      statusCodeGroups: {
        clientError: '4xx',
        serverError: '5xx',
      },
        recipient: 'Recipient',
        defaultRecipient: 'Default recipient',
        customRecipient: 'Custom recipient',
        customRecipientPlaceholder: 'Enter custom mail id',
      },
      thresholdBreach: {
        title: 'Threshold Breach Configuration',
        consecutiveBreachesRequired: 'Consecutive Breaches Required',
        recipient: 'Recipient',
        defaultRecipient: 'Default recipient',
        customRecipient: 'Custom recipient',
        customRecipientPlaceholder: 'Enter custom mail id',
      },
      sslDomainChecks: {
        title: 'SSL & Domain Checks',
        sslCertificateDomainChecks: 'SSL Certificate & Domain Checks',
        sslErrors: 'SSL Errors',
        sslExpiry: 'SSL Expiry',
        domainExpiry: 'Domain Expiry',
        notifyWhenExpiresIn: 'Notify when expires in (days)',
        recipient: 'Recipient',
        defaultRecipient: 'Default recipient',
        customRecipient: 'Custom recipient',
        customRecipientPlaceholder: 'Enter custom mail id',
      },
    },
    monitorTypes: {
      scraper: 'Scraper',
      httpMonitor: 'HTTP/Website Monitoring',
      syntheticMonitor: 'Synthetic Monitoring',
      scraperDescription: 'Scrapes websites to track load times, layout shifts, and overall page performance.',
      httpMonitorDescription: 'Use an HTTP(S) monitor to automatically track uptime, response time, SSL status, and domain health',
      syntheticMonitorDescription: 'Tests websites, apps, and APIs by using automated scripts to simulate real user journeys and transactions',
    },
    buttons: {
      createScraper: 'Create Scraper',
      createMonitor: 'Create Monitor',
    },
    labels: {
      selectType: "Select the type of configuration you'd like to create.",
      scheduleInterval: 'Schedule Interval',
      scheduleFrequency: 'Schedule Frequency',
      activeHoursStart: 'Active Hours Start',
      activeHoursEnd: 'Active Hours End',
      consecutiveBreachesRequired: 'Consecutive Breaches Required',
      alertSuppressionWindow: 'Alert Suppression Window(mins)',
    },
    messages: {
      scraperCreated: 'Scraper configuration created successfully!',
      monitorCreated: 'HTTP Monitor created successfully!',
      monitorUpdated: 'Monitor updated successfully!',
      failedToCreateScraper: 'Failed to create scraper configuration. Please try again.',
      failedToCreateMonitor: 'Failed to create monitor. Please try again.',
      syntheticComingSoon: 'Synthetic test creation will be available soon.',
      syntheticMonitorCreated: 'Synthetic monitor created successfully.',
      syntheticApiNotAvailable: 'Synthetic monitor API is not available yet.',
    },
    validation: {
      scheduleTimesMustBeUnique: 'Schedule times must be unique.',
      monitorNameRequired: 'Monitor name is required.',
      websiteUrlRequired: 'Website URL is required.',
      usernameRequired: 'Username is required.',
      passwordRequired: 'Password is required.',
      allRequiredFieldsEmpty: 'All required fields must be filled. Please enter Monitor name, Website URL, Username and Password.',
      monitorNameLooksLikeUrl: 'Monitor name should be a short name (e.g. "Shark Ninja"), not a URL. Please enter the Website URL in the Website URL field below.',
    },
  },

  // Page Scrapper
  pageScrapper: {
    title: 'Page Scraper',
    subtitle: 'Monitor web page elements and performance',
    tabs: {
      overview: 'Overview',
      performanceMetrics: 'Performance Metrics',
      errorAnalysis: 'Error Analysis',
      functionChecks: 'Function Checks',
      urlManagement: 'URL Management',
    },
    labels: {
      url: 'URL',
      clientName: 'Client Name',
      totalUrlsScraped: 'Total URLs Scraped',
      totalMetricsCollected: 'Total Metrics Collected',
      metricsPerUrl: 'Metrics per URL',
      avgFirstContentfulPaint: 'Avg First Contentful Paint',
      avgLargestContentfulPaint: 'Avg Largest Contentful Paint',
      avgTimeToFirstByte: 'Avg Time To First Byte',
      errorThreshold: 'Error Threshold (%)',
      scheduleType: 'Schedule Type',
      urlConfiguration: 'URL Configuration',
      interval: 'Interval (minutes)',
      activeHours: 'Active Hours',
    },
    actions: {
      view: 'View',
      viewDetails: 'View Details',
      runScrapping: 'Run Scrapping',
      stopScrapping: 'Stop Scrapping',
      restartScrapping: 'Restart Scrapping',
    },
    detail: {
      title: 'URL Details',
      subtitle: 'Configuration and performance details for this URL',
      backToUrlManagement: 'Back to Monitors',
      placeholderTitle: 'Details Coming Soon',
      placeholderMessage: 'Detailed information and metrics for this URL configuration will be displayed here.',
      sections: {
        overview: 'Overview',
        configuration: 'Configuration',
        performance: 'Performance Metrics',
        elements: 'Element Checks',
        errors: 'Error Analysis',
        history: 'Run History',
      },
    },
    placeholders: {
      enterUrl: 'https://example.com',
      enterClientName: 'Enter client name',
    },
    messages: {
      noConfigurations: 'No URL Configurations',
      getStarted: 'Get started by adding your first URL configuration to begin monitoring web pages.',
      configurationSaved: 'URL configuration saved successfully',
      configurationDeleted: 'URL configuration deleted successfully',
      failedToSave: 'Failed to save URL configuration. Please try again.',
      failedToDelete: 'Failed to delete URL configuration. Please try again.',
      failedToStart: 'Failed to start scraper. Please try again.',
      failedToStop: 'Failed to stop scraper. Please try again.',
      scrapingStartedSuccessfully: 'Scraping started successfully',
      scrapingStoppedSuccessfully: 'Scraping stopped successfully',
    },
    validation: {
      urlRequired: 'URL is required',
      invalidUrl: 'URL must use HTTPS protocol (https://)',
      clientNameRequired: 'Client Name is required',
    },
    tooltips: {
      customRetryAttempts: 'Number of times to retry if the page fails to load',
      customTimeout: 'Maximum time to wait for the page to load before timing out',
      waitAfterLoad: 'Time to wait after page load for dynamic content to render',
      scheduleFrequency: 'How often the monitoring or scraping task runs',
      consecutiveBreaches: 'Consecutive performance breaches required before triggering an alert',
      alertSuppression: 'Time during which repeat alerts are temporarily suppressed',
      onErrorActions: 'Actions to perform when a scraping error occurs (retry, logs, screenshots)',
    },
    emptyStates: {
      overview: 'Overview content will be added here. This tab will display a summary of scraping activities and key metrics.',
      performanceMetrics: 'Performance analytics and detailed metrics will be displayed here. Monitor scraping speed, response times, efficiency metrics, success rates, data volume, and scraping patterns.',
      errorAnalysis: 'Error analysis and diagnostics will appear here. Track error patterns, failure rates, and root cause analysis for scraping issues.',
      functionChecks: 'Function checks and validation results will be displayed here. Monitor element presence, resource loading, AJAX calls, and critical functionality tests.',
    },
    charts: {
      elementPresenceSummary: 'Element Presence Summary',
      elementPresenceSubtitle: 'Monitor the presence of critical UI elements across all page checks',
    },
    siteConfiguration: {
      link: 'Click to view Site Configuration',
      title: 'Site Configuration',
      createdOn: 'Created On',
      createdBy: 'Created By',
      timeout: 'Timeout',
      retryAttempts: 'Retry Attempts',
      waitAfterLoad: 'Wait After Load',
      elementTemplate: 'Element Template',
      scheduleType: 'Schedule Type',
      errorThreshold: 'Error Threshold',
      fcpThresholds: 'FCP Thresholds',
      lcpThresholds: 'LCP Thresholds',
      ttfbThresholds: 'TTFB Thresholds',
      tbtThresholds: 'TBT Thresholds',
      clsThresholds: 'CLS Thresholds',
      siThresholds: 'SI Thresholds',
      screenshots: 'Screenshots',
      checkResources: 'Check Resources',
      monitorAjax: 'Monitor AJAX',
      criticalElements: 'Critical Elements',
      emailRecipient: 'Email Recipient',
      enabled: 'Enabled',
      disabled: 'Disabled',
    },
  },

  // Sales Analyzer
  salesAnalyzer: {
    title: 'Sales Analytics',
    description: 'Track daily order outcomes across domains and channels to identify failure trends, operational risks, and business impact.',
    emptyState: {
      title: 'No Data to be analyzed!',
      message: 'Start by adding your first domain and analyze your sales performance.',
      actionLabel: 'Add New Domain',
    },
    domainConfiguration: {
      title: 'Domain Configuration',
      description: 'Configure data sources and visualizations for your sales performance tracking dashboard.',
      steps: {
        domainConfiguration: 'Domain Configuration',
        preview: 'Preview',
      },
      fields: {
        domainName: 'Domain Name',
        failedOrderThreshold: 'Failed order threshold',
        alertRecipient: 'Alert recipient',
        stuckOrderThreshold: 'Stuck order Threshold',
      },
      placeholders: {
        domainName: 'Enter Domain Name',
        failedOrderThreshold: 'Enter threshold value',
        alertRecipient: 'Enter mail ID',
        stuckOrderThreshold: 'Enter Threshold value',
      },
      fileUpload: {
      label: 'Drag and Drop or ',
      chooseFile: 'Choose File',
        supportedFormats: 'Supported formats: CSV',
        maxSize: 'Maximum size: 5MB',
        invalidFormat: 'Please upload a CSV file',
        uploadedJustNow: 'Uploaded just now',
      },
      tableExample: {
        title: 'Table Example',
        description: 'You can download the attached example and use them as a starting point for your own file.',
        download: 'Download',
      },
      csvMapping: {
        title: 'CSV Mapping',
        tooltip: 'Enable CSV mapping to map the CSV columns to the fields in the form',
        description: 'Select the columns you\'d like to import, then map the target fields to your spreadsheet.',
        tableHeaders: {
          select: 'Select',
          targetField: 'Target Field',
          spreadsheet: 'Your Spreadsheet',
        },
      },
      validation: {
        invalidEmail: 'Please enter a valid email address',
      },
    },
    preview: {
      title: '{Domain_Name} Preview',
      metrics: {
        successOrders: 'Success Orders',
        failedOrders: 'Failed Orders',
        stuckOrders: 'Stuck Orders',
        stuckOrdersResolved: 'Stuck Orders Resolved',
        failureRate: 'Failure Rate (%)',
      },
    },
    charts: {
      failedOrderPercentage: 'FAILED ORDER PERCENTAGE',
      stuckOrderMetrics: 'STUCK ORDER METRICS',
      successVsFailedOrders: 'SUCCESS VS FAILED ORDERS',
    },
    editDomain: 'Edit Domain',
    deleteDomain: 'Delete Domain',
    deleteDomainTitle: 'DELETE DOMAIN',
    deleteDomainMessage: 'Are you sure you want to delete the domain "{domainName}"?',
    selectDomain: 'Select Domain',
    dashboard: {
      noData: {
        title: 'No Data Available',
        message: 'There is no sales analytics data available for the selected domain. Please add daily orders to get started.',
        addDailyOrdersButton: 'Add Daily Orders',
      },
      domainLabel: 'Domain:',
      chooseDomain: 'Choose Domain',
      addButton: 'Add',
      addDailyOrders: 'Daily Orders',
      addDomain: 'Domain',
      timeWindow: {
        sevenDays: '7D',
        fifteenDays: '15D',
        oneMonth: '1M',
        all: 'All',
      },
      changeLabel: ' vs Previous Day',
      kpiFallback: {
        successOrders: 'SUCCESS ORDERS',
        failedOrders: 'FAILED ORDERS',
        stuckOrders: 'STUCK ORDERS',
        failedOrdersPercentage: 'FAILED ORDERS PERCENTAGE',
      },
      table: {
        date: 'Date',
        successOrders: 'Success orders',
        failedOrders: 'Failed Orders',
        stuckOrders: 'Stuck Orders',
        stuckOrdersResolved: 'Stuck orders Resolved',
        failureRate: 'Failure Rate (%)',
        export: 'Export',
      },
      chartAxis: {
        percentage: 'Percentage',
        date: 'Date',
        orders: 'Orders',
        value: 'Value',
      },
      errorPrefix: 'Error: ',
    },
  },

  // Log Analyzer
  logAnalyzer: {
    title: 'SFCC Log Monitor',
    description: 'Real-time analysis of application logs to identify frequent, severe, and time-based error patterns.',
    tabs: {
      operations: 'Overview',
      troubleshooting: 'Troubleshooting',
      analytics: 'Trends',
      jobAnalysis: 'Job Analytics',
    },
    environment: {
      label: 'Environment',
      staging: 'Staging',
      production: 'Production',
    },
    filters: {
      domain: 'Select Domain',
      timeWindow: 'Time Window',
      severity: 'Severity Filter',
      stepInterval: 'Step Interval',
      chartType: 'Chart Type',
      resetFilters: 'Reset Filters',
      applyFilters: 'Apply Filters',
    },
    timeWindows: {
      oneMinute: '1m',
      twoMinutes: '2m',
      fiveMinutes: '5m',
      tenMinutes: '10m',
      fifteenMinutes: '15 minutes',
      thirtyMinutes: '30m',
      oneHour: '1 hour',
      twoHours: '2 hours',
      threeHours: '3 hours',
      sixHours: '6 hours',
      twelveHours: '12 hours',
      twentyFourHours: '24 hours',
      sevenDays: '7 days',
      thirtyDays: '30 days',
    },
    stepIntervals: {
      oneMinute: '1 minute',
      fiveMinutes: '5 minutes',
      tenMinutes: '10 minutes',
      thirtyMinutes: '30 minutes',
      oneHour: '1 hour',
    },
    severityLevels: {
      all: 'All',
      debug: 'debug',
      error: 'error',
      warn: 'warn',
      fatal: 'fatal',
      info: 'info',
    },
    chartTypes: {
      barVertical: 'Bar (Vertical)',
      barHorizontal: 'Bar (Horizontal)',
      pie: 'Pie',
    },
    domains: {
      mx: 'Sites-MX-SharkNinja-Site',
      ca: 'Sites-CA-SharkNinja-Site',
      usTiktok: 'Sites-US-Shark-Tiktok-Site',
      us: 'Sites-US-SharkNinja-Site',
    },
    sections: {
      detailedErrorMessages: {
        title: '1. Detailed Error Messages',
        categoryFilter: 'Category Filter (optional)',
        categoryPlaceholder: 'e.g., custom.jsonl',
        limit: 'Limit',
        noErrorMessages: 'No error messages found for the selected filters. Try adjusting your search criteria.',
      },
      errorBreakdown: {
        title: '2. Error Breakdown Analysis',
        byCategory: 'By Category',
        byService: 'By Service',
        byRequestType: 'By Request Type',
        groupedBy: 'Errors grouped by service type',
        severityFilter: 'Severity Filter',
        noServiceData: 'No service data available for the selected time window and filters.',
      },
    },
    priority: {
      high: 'Priority: HIGH',
      description: 'Investigate specific errors and affected systems',
    },
    placeholders: {
      comingSoon: 'Coming soon',
    },
    dashboard: {
      lastUpdated: 'Last updated: {timestamp}',
      dataSource: 'Data source: Domain Analytics API',
      queriesNote: 'Powered by CloudWatch Insights',
      metrics: {
        totalLogEntries: 'Total Log Entries',
        totalErrors: 'Total Errors',
        uniqueErrorTypes: 'Unique Error Types',
        errorRate: 'Error Rate',
      },
    },
    operations: {
      title: 'Operations Dashboard - Real-time Monitoring',
      subtitle: 'Priority: HIGHEST - Live error tracking, frequency trends, and alerts',
      liveErrorFrequency: {
        title: 'Live Error Rate',
        subtitle: 'Last {timeWindow}',
        totalErrors: 'Total Errors',
        dataPoints: 'Data Points',
        peakCount: 'Peak Count',
        avgCount: 'Avg Count',
      },
      severityDistribution: {
        title: 'Severity Distribution',
        subtitle: 'Current Severity Distribution',
        deprecationWarning: 'The keyword arguments have been deprecated and will be removed in a future release. Use config instead to specify chart configuration options.',
      },
      domainHealth: {
        title: 'Domain Health Status',
        columns: {
          domain: 'Domain',
          errorCount: 'Error Count',
          lastError: 'Last Error',
          healthScore: 'Health Score',
        },
      },
      topErrors: {
        mainTitle: 'Error Analysis',
        title: 'Error Message',
        subtitle: 'Highlights the most frequently occurring SFCC errors within the current timeframe.',
        filterBySeverity: 'Filter by Severity',
        limit: 'Limit',
        exportToCsv: 'Export to CSV',
        noErrors: 'No active errors detected in the selected time window. System is operating normally.',
        noErrorsTryFilters: 'No errors found. Try changing the filters or search keyword.',
        columns: {
          severity: 'Severity',
          message: 'Error Message',
          count: 'Count',
          firstOccurrence: 'First Occurrence',
          latestOccurrence: 'Last Occurrence',
          affectedDomains: 'Affected Domains',
        },
        latestError: 'LATEST ERROR',
        copy: 'Copy',
        noErrorsFound: 'No errors found',
      },
    },
    analytics: {
      title: 'Historical Error Rate Trend',
      subtitle: 'Visualizes how SFCC error rates have changed over time to reveal long-term patterns.',
      historicalFrequency: {
        title: 'Historical Error Frequency',
        chartTitle: 'Historical Frequency - {domain} ({timeWindow})',
        totalErrors: 'Total Errors',
        dataPoints: 'Data Points',
        peakCount: 'Peak Count',
        avgCount: 'Avg Count',
      },
      domainComparison: {
        title: 'Domain Comparison',
        chartTitle: 'Error Count by Domain ({timeWindow})',
      },
      severityDistribution: {
        title: 'Severity Distribution',
        chartTitle: 'Severity Distribution - {severity}',
      },
      topErrorMessages: {
        mainTitle: 'Top Error messages by Domain and Severity',
        subTitle: 'Provides a detailed view of dominant SFCC error messages, segmented by domain and severity level.',
        title: 'Error messages',
        categoryFilter: 'Category Filter (optional)',
        categoryPlaceholder: 'e.g., custom.jsonl',
        limit: 'Limit',
        columns: {
          category: 'Category',
          message: 'Message',
          count: 'Count',
          firstSeen: 'First Occurrence',
          lastSeen: 'Last Occurrence',
        },
      },
      deprecationWarning: 'The keyword arguments have been deprecated and will be removed in a future release. Use config instead to specify chart configuration options.',
    },
    jobAnalysis: {
      title: 'Job Analytics Dashboard',
      subtitle: 'Analyze job execution patterns and failures',
      topCategories: {
        title: 'Top 20 Categories',
        chartTitle: 'Top 20 Categories: {domain} | {timeWindow}',
        totalCategories: 'Total Categories',
        totalCount: 'Total Count',
        topCategoryPercent: 'Top Category %',
        columns: {
          category: 'category',
          count: 'count',
          percentage: 'percentage',
          affectedDomains: 'affected_domains',
        },
      },
      requestTypeBreakdown: {
        title: 'Request Type Breakdown',
        chartTitle: 'Request Type Breakdown: {domain} | {timeWindow}',
        totalRequestTypes: 'Total Request Types',
        totalCount: 'Total Count',
        topTypePercent: 'Top Type %',
        columns: {
          requestType: 'request_type',
          count: 'count',
          percentage: 'percentage',
          affectedDomains: 'affected_domains',
        },
      },
      jobFailures: {
        title: 'Job Failures by Category',
        chartTitle: 'Job Failures by Category: {domain} | {timeWindow}',
        jobCategories: 'Job Categories',
        totalFailures: 'Total Failures',
        affectedDomains: 'Affected Domains',
        columns: {
          category: 'category',
          count: 'count',
          percentage: 'percentage',
          affectedDomains: 'affected_domains',
        },
      },
    },
  },

  // Alerts page
  alerts: {
    title: 'Alerts',
    subtitle: 'Monitor and analyze real-time performance of POS and Z500 devices across all store locations globally. Track RTT, detect outages, and ensure uptime from a centralized view.',
    tableTitle: 'ALERTS',
    labels: {
      alertId: 'Alert ID',
      deviceId: 'Device ID',
      conditionBreached: 'Condition Breached',
      siteName: 'Site Name',
      severity: 'Severity',
      status: 'Status',
      createdOn: 'Created On',
      timestamp: 'Timestamp',
      message: 'Message',
    },
    metrics: {
      activeAlerts: 'ACTIVE ALERTS',
      criticalAlerts: 'CRITICAL ALERTS',
      devicesAffected: 'DEVICES AFFECTED',
      unacknowledgedAlerts: 'UNACKNOWLEDGED ALERTS',
      resolvedAlerts: 'RESOLVED ALERTS',
    },
    filterOptions: {
      allStatus: 'All Status',
      active: 'Active',
      acknowledged: 'Acknowledged',
      resolved: 'Resolved',
    },
    actions: {
      raiseIncident: 'Raise Incident',
      refresh: 'Refresh',
      export: 'Export',
    },
    messages: {
      noAlerts: 'No alerts found',
      incidentRaised: 'Incident raised for {alertId}',
      exportError: 'Error exporting alerts',
    },
    csvHeaders: {
      alertId: 'Alert ID',
      deviceId: 'Device ID',
      condition: 'Condition',
      siteName: 'Site Name',
      severity: 'Severity',
      createdOn: 'Created On',
      status: 'Status',
    },
  },

  // Incidents page
  incidents: {
    title: 'Incidents',
    subtitle: 'View and manage incidents',
    labels: {
      incidentId: 'Incident ID',
      siteUrl: 'Site URL',
      status: 'Status',
      severity: 'Severity',
      timestamp: 'Timestamp',
      description: 'Description',
    },
    messages: {
      noIncidents: 'No incidents found',
    },
    dashboard: {
      title: 'Incident Overview',
      subtitle: 'Track, analyze, and resolve outages across your monitored sites.',
      metrics: {
        totalIncidents: 'TOTAL INCIDENTS',
        active: 'ACTIVE',
        resolved: 'RESOLVED',
        averageResolutionTime: 'AVERAGE RESOLUTION TIME',
      },
    },
    detail: {
      title: 'Incident Overview',
      subtitle: 'Track, analyze, and resolve outages across your monitored sites',
      downloadResponse: 'Download Response',
      sections: {
        request: 'REQUEST',
        response: 'RESPONSE',
        activityLog: 'ACTIVITY LOG',
      },
      tabs: {
        urlMethod: 'URL & Method',
        header: 'Header',
        headers: 'Headers',
        details: 'Details',
      },
      metrics: {
        status: 'STATUS',
        rootCause: 'ROOT CAUSE',
        duration: 'DURATION',
      },
      activityLog: {
        rootCause: 'Root Cause',
        time: 'Time',
        noActivity: 'No activity recorded',
      },
      statusLabels: {
        resolved: 'RESOLVED',
        open: 'OPEN',
        resolvedOn: 'Resolved on {date}',
        ongoing: 'Ongoing',
      },
    },
  },

  // Triage (formerly Incidents)
  triage: {
    title: 'Triage',
    subtitle: 'View and manage incidents',
    labels: {
      incidentId: 'Incident ID',
      siteUrl: 'Site URL',
      status: 'Status',
      severity: 'Severity',
      timestamp: 'Timestamp',
      description: 'Description',
      origin: 'Origin',
    },
    messages: {
      noIncidents: 'No incidents found',
    },
    filters: {
      title: 'FILTERS',
      alerts: {
        alertId: 'Alert ID',
        siteId: 'Site ID',
        siteUrl: 'Site URL',
        origin: 'Origin',
        severity: 'Severity',
        createdAt: 'Created At',
        startDate: 'Start Date',
        endDate: 'End Date',
        search: 'Search',
        includeArchived: 'Include Archived',
        resetFilters: 'Reset Filters',
        applyFilters: 'Apply Filters',
      },
      incidents: {
        incidentId: 'Incident ID',
        siteUrl: 'Site URL',
        origin: 'Origin',
        severity: 'Severity',
        search: 'Search',
        status: 'State',
        startDate: 'Start Date',
        endDate: 'End Date',
        includeArchived: 'Include Archived',
        raisedFromAlert: 'Raised From Alert',
        resetFilters: 'Reset Filters',
        applyFilters: 'Apply Filters',
      },
      placeholders: {
        alertId: 'e.g., ALR20251205120000',
        siteId: 'Enter site ID',
        siteUrl: 'Enter site URL',
        origin: 'MONITOR/SCRAPER/LOG_ANALYZER',
        severity: 'critical/high/medium/low',
        createdAt: 'YYYY-MM-DD',
        search: 'Search pattern',
        incidentId: 'e.g., INC20251205120000',
        status: 'Open, Resolved',
        dateRange: 'Select date range',
      },
      options: {
        all: 'All',
        allSeverities: 'All Severities',
        allStatuses: 'All Statuses',
        allOrigins: 'All Origins',
        critical: 'Critical',
        high: 'High',
        medium: 'Medium',
        low: 'Low',
        open: 'Open',
        resolved: 'Resolved',
        monitor: 'MONITOR',
        scraper: 'SCRAPER',
        logAnalyzer: 'LOG ANALYZER',
        raisedFromAlert: 'Raised from alerts',
        notFromAlert: 'Not from alerts',
        allIncidents: 'All incidents',
      },
    },
    dashboard: {
      title: 'Triage Overview',
      subtitle: 'Track, analyze, and resolve outages across your monitored sites.',
      metrics: {
        totalIncidents: 'TOTAL INCIDENTS',
        active: 'ACTIVE',
        resolved: 'RESOLVED',
        averageResolutionTime: 'AVERAGE RESOLUTION TIME',
      },
    },
    detail: {
      title: 'Triage Overview',
      subtitle: 'Track, analyze, and resolve outages across your monitored sites',
      downloadResponse: 'Download Response',
      sections: {
        request: 'REQUEST',
        response: 'RESPONSE',
        activityLog: 'ACTIVITY LOG',
        logAnalyzer: 'LOG ANALYZER DATA',
        domains: 'AFFECTED DOMAINS',
        thresholdComparison: 'THRESHOLD COMPARISON',
        topErrors: 'TOP ERRORS',
      },
      tabs: {
        urlMethod: 'URL & Method',
        header: 'Header',
        headers: 'Headers',
        details: 'Details',
      },
      metrics: {
        status: 'STATUS',
        rootCause: 'ROOT CAUSE',
        duration: 'DURATION',
      },
      activityLog: {
        rootCause: 'Root Cause',
        time: 'Time',
        noActivity: 'No activity recorded',
      },
      statusLabels: {
        resolved: 'RESOLVED',
        open: 'OPEN',
        resolvedOn: 'Resolved on {date}',
        ongoing: 'Ongoing',
      },
      changeState: {
        title: 'Change Incident State',
        fields: {
          newState: 'New State',
          comment: 'Comment',
        },
        placeholders: {
          selectState: 'Select state',
          comment: 'Enter a comment describing the state change',
        },
        buttons: {
          changeState: 'Change State',
          changing: 'Changing...',
        },
        validation: {
          commentRequired: 'Comment is required',
          differentState: 'Please select a different state',
        },
        errors: {
          failed: 'Failed to change incident state',
        },
        success: 'Incident state changed successfully',
      },
      logAnalyzer: {
        alertType: {
          errorSpike: 'Error Spike',
          highVolume: 'High Error Volume',
          multipleDomains: 'Multiple Domains Affected',
        },
        metrics: {
          currentCount: 'Current Count',
          previousCount: 'Previous Count',
          increasePercentage: 'Increase Percentage',
          totalErrors: 'Total Errors',
          errorCount: 'Error Count',
          affectedDomains: 'Affected Domains',
          totalDomains: 'Total Domains',
        },
        domain: {
          domainName: 'Domain Name',
          domainUrl: 'Domain URL',
          lastError: 'Last Error',
          firstError: 'First Error',
          errorCount: 'Error Count',
        },
        error: {
          errorMessage: 'Error Message',
          count: 'Count',
          percentage: 'Percentage',
          firstOccurrence: 'First Occurrence',
          latestOccurrence: 'Latest Occurrence',
          severityLevel: 'Severity Level',
        },
        threshold: {
          thresholdName: 'Threshold Name',
          thresholdValue: 'Threshold Value',
          actualValue: 'Actual Value',
          exceededBy: 'Exceeded By',
          exceededByPercentage: 'Exceeded By %',
          percentageOfThreshold: '% of Threshold',
          thresholdExceeded: 'Threshold Exceeded',
        },
      },
    },
  },

  // Log Browser page
  logBrowser: {
    title: 'Log Browser',
    subtitle: 'Search and analyze application logs',
    tableTitle: 'Log Entries',
    labels: {
      timestamp: 'Timestamp',
      level: 'Level',
      message: 'Message',
      source: 'Source',
      logSource: 'Log Source',
      selectLogEntry: 'Select log entry for Quick View & Records:',
    },
    metrics: {
      totalLogs: 'Total Logs',
      errorsToday: 'Errors Today',
      warningsToday: 'Warnings Today',
      avgLatency: 'Avg. Latency',
      dataSources: 'Data Sources',
    },
    logSources: {
      fullDataLogs: 'full_data_logs.jsonl',
      errorLogs: 'error_logs.jsonl',
      accessLogs: 'access_logs.jsonl',
      systemLogs: 'system_logs.jsonl',
    },
    searchFields: {
      basic: 'Basic',
      decodedResponse: 'Decoded Response',
      message: 'Message',
      userAgent: 'User Agent',
      source: 'Source',
      details: 'Details',
    },
    filterOptions: {
      allLevels: 'All Levels',
      allSources: 'All Sources',
    },
    actions: {
      refreshLogs: 'Refresh Logs',
      quickTest: 'Quick Test',
      viewRecords: 'View Records',
      active: 'Active',
    },
    placeholders: {
      selectLogEntry: 'Select a log entry or click a table row...',
    },
    messages: {
      logsRefreshed: 'Logs refreshed!',
      quickTestInitiated: 'Quick test initiated for all 11 active alert patterns!',
    },
    quickView: {
      title: 'Quick View - Logs {count}',
      subtitle: 'Click on any table row or select from dropdown to view log details. Use the accordion to expand/collapse JSON data.',
    },
    recordsModal: {
      title: 'Records',
    },
  },

  // Breadcrumbs
  breadcrumbs: {
    home: 'Monitors',
    monitors: 'Monitors',
    websites: 'Websites',
    syntheticMonitors: 'Synthetic Monitors',
    scraper: 'Scraper',
    salesAnalyzer: 'Sales Analyzer',
    alerts: 'Alerts',
    incidents: 'Incidents',
    triage: 'Triage',
    logAnalyzer: 'Log Analyzer',
    pageScrapper: 'Page Scraper',
    urlManagement: 'URL Management',
    urlDetails: 'URL Details',
    dashboard: 'Dashboard',
    logBrowser: 'Log Browser',
    details: 'Details',
    edit: 'Edit',
    new: 'New',
    addOrEdit: 'Add/Edit',
  },

  // Status badges
  status: {
    inProgress: 'In Progress',
    onHold: 'On Hold',
    active: 'Active',
    inactive: 'Inactive',
    disabled: 'Disabled',
    pending: 'Pending',
    resolved: 'Resolved',
    open: 'Open',
    closed: 'Closed',
    up: 'Up',
    down: 'Down',
    warning: 'Warning',
    critical: 'Critical',
    info: 'Info',
    success: 'Success',
    error: 'Error',
  },

  // Error messages
  errors: {
    generic: 'An error occurred. Please try again.',
    networkError: 'Unable to connect to server. Please check your connection and try again.',
    unauthorized: 'You are not authorized to perform this action.',
    notFound: 'The requested resource was not found.',
    serverError: 'Server error. Please try again later.',
    validationError: 'Please check your input and try again.',
    timeout: 'Request timed out. Please try again.',
  },

  // Success messages
  success: {
    saved: 'Saved successfully',
    deleted: 'Deleted successfully',
    updated: 'Updated successfully',
    created: 'Created successfully',
  },
};

/**
 * Helper function to replace placeholders in strings
 * Example: replacePlaceholders('Welcome {name}!', { name: 'John' }) => 'Welcome John!'
 */
export function replacePlaceholders(
  template: string,
  values: Record<string, string | number>
): string {
  return template.replace(/\{(\w+)\}/g, (match, key) => {
    return values[key]?.toString() || match;
  });
}

